import React from 'react';
import { FileText, Download, Loader2, AlertTriangle } from 'lucide-react';

export interface FicheDocument {
    id: string;
    kind: string | null;
    file_name: string;
    mime_type?: string | null;
    file_size?: number | null;
    created_at?: string | null;
}

/** Les trois natures que le parcours exige, dans l'ordre où on les rencontre. */
const NATURES: { kind: string; label: string; aide: string }[] = [
    { kind: 'plaquette', label: 'Plaquette commerciale', aide: 'Envoyée au client au tout début — elle fait foi sur les formules proposées.' },
    { kind: 'devis', label: 'Devis', aide: 'Produit dans votre logiciel habituel : la plateforme ne le génère pas, elle le conserve.' },
    { kind: 'devis_signe', label: 'Devis signé', aide: "C'est lui qui engage l'établissement et déclenche la collecte des menus." },
];

const fmtPoids = (o?: number | null) => (typeof o === 'number' && o > 0 ? `${Math.round(o / 1024)} Ko` : '');
const fmtJour = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return ''; }
};

/**
 * Les justificatifs du dossier, rangés par nature plutôt qu'en vrac chronologique : la question qu'on se
 * pose ici n'est pas « qu'a-t-on déposé récemment ? » mais « le devis signé est-il arrivé ? ».
 *
 * Une nature peut porter plusieurs fichiers — un devis corrigé, une plaquette de la nouvelle saison. Le plus
 * récent est mis en avant, les précédents restent accessibles : on n'écrase jamais un justificatif, il a pu
 * être envoyé à un client tel quel.
 */
export function PipelineDocuments({ dossierId, documents }: { dossierId: string; documents: FicheDocument[] }) {
    const [ouverture, setOuverture] = React.useState<string | null>(null);
    const [erreur, setErreur] = React.useState('');

    // Le fichier est servi par une route authentifiée : on ne peut pas se contenter d'un lien, il faut
    // porter le jeton. Même geste que la fiche partenaire des Extras.
    const ouvrir = async (docId: string, fileName: string) => {
        setOuverture(docId);
        setErreur('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/suivi-privatisation/${dossierId}/documents/${docId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Document introuvable.');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'document';
            a.click();
            // Libéré au tour de boucle suivant : révoquer immédiatement annulerait le téléchargement.
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (e: any) {
            setErreur(e?.message || 'Téléchargement impossible.');
        } finally {
            setOuverture(null);
        }
    };

    const autres = documents.filter((d) => !d.kind || !NATURES.some((n) => n.kind === d.kind));

    return (
        <div className="space-y-3">
            {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}

            {NATURES.map((nature) => {
                const fichiers = documents.filter((d) => d.kind === nature.kind);
                const present = fichiers.length > 0;
                return (
                    <div key={nature.kind} className={`rounded-xl border p-3 ${present ? 'border-border bg-surface' : 'border-dashed border-border'}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-content flex items-center gap-1.5">
                                    <FileText size={13} className={present ? 'text-brand-600 dark:text-brand-400' : 'text-content-muted'} />
                                    {nature.label}
                                </p>
                                <p className="text-[10px] text-content-muted mt-0.5 leading-relaxed">{nature.aide}</p>
                            </div>
                            {!present && (
                                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-surface-soft text-content-muted whitespace-nowrap">
                                    pas encore reçu
                                </span>
                            )}
                        </div>
                        {present && (
                            <div className="mt-2 space-y-1">
                                {fichiers.map((d, i) => (
                                    <button key={d.id} type="button" onClick={() => ouvrir(d.id, d.file_name)}
                                        disabled={ouverture === d.id}
                                        className="w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-lg border border-border hover:border-brand-500/40 hover:bg-surface-soft transition-colors text-left disabled:opacity-50">
                                        <span className="text-[11px] text-content truncate">
                                            {d.file_name}
                                            {i > 0 && <span className="text-content-muted"> — version précédente</span>}
                                        </span>
                                        <span className="flex items-center gap-2 shrink-0 text-[10px] text-content-muted">
                                            {fmtPoids(d.file_size)} {fmtJour(d.created_at)}
                                            {ouverture === d.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}

            {autres.length > 0 && (
                <div className="rounded-xl border border-border p-3">
                    <p className="text-xs font-bold text-content flex items-center gap-1.5">
                        <AlertTriangle size={13} className="text-content-muted" /> Autres documents
                    </p>
                    {/* Documents déposés hors parcours (ou avant l'existence du module, donc sans nature). */}
                    <div className="mt-2 space-y-1">
                        {autres.map((d) => (
                            <button key={d.id} type="button" onClick={() => ouvrir(d.id, d.file_name)}
                                className="w-full flex items-center justify-between gap-3 px-2 py-1.5 rounded-lg border border-border hover:border-brand-500/40 hover:bg-surface-soft transition-colors text-left">
                                <span className="text-[11px] text-content truncate">{d.file_name}</span>
                                <Download size={12} className="text-content-muted shrink-0" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
